import React, { useState, useEffect } from 'react';
// @ts-ignore
import { Helmet } from 'react-helmet';
//@ts-ignore
import { FaClock, FaGlobe, FaExchangeAlt, FaGlobeAmericas, FaHourglassHalf, FaMapMarkedAlt } from 'react-icons/fa';
import Clock from './Clock';
import TimeZoneMap from './TimeZoneMap';

interface TimeZone {
  label: string;
  value: string;
  offset: string;
}

// Popular time zones with their offsets
const timeZones: TimeZone[] = [
  { label: 'UTC', value: 'UTC', offset: 'UTC+00:00' },
  { 
    label: 'London (GMT/BST)', 
    value: 'Europe/London', 
    offset: new Date().getTimezoneOffset() === 0 ? 'UTC+01:00 (BST)' : 'UTC+00:00 (GMT)' 
  },
  { label: 'New York (ET)', value: 'America/New_York', offset: 'UTC-05:00' },
  { label: 'Los Angeles (PT)', value: 'America/Los_Angeles', offset: 'UTC-08:00' },
  { label: 'Tokyo (JST)', value: 'Asia/Tokyo', offset: 'UTC+09:00' },
  { label: 'Sydney (AEST)', value: 'Australia/Sydney', offset: 'UTC+10:00' },
  { label: 'Dubai (GST)', value: 'Asia/Dubai', offset: 'UTC+04:00' },
  { label: 'Moscow (MSK)', value: 'Europe/Moscow', offset: 'UTC+03:00' },
  { label: 'Paris (CET)', value: 'Europe/Paris', offset: 'UTC+01:00' },
  { label: 'Berlin (CET)', value: 'Europe/Berlin', offset: 'UTC+01:00' },
  { label: 'Singapore (SGT)', value: 'Asia/Singapore', offset: 'UTC+08:00' },
  { label: 'India (IST)', value: 'Asia/Kolkata', offset: 'UTC+05:30' }
];

const TimeUtility: React.FC = () => {
  const [currentTime, setCurrentTime] = useState<Date>(new Date());
  const [preferredTimeZone, setPreferredTimeZone] = useState<string>('UTC');
  const [inputTime, setInputTime] = useState<string>('');
  const [convertedTimes, setConvertedTimes] = useState<{ [key: string]: string }>({});
  const [activeTab, setActiveTab] = useState<'current' | 'timezone' | 'offset' | 'map'>('current');
  const [fromTimeZone, setFromTimeZone] = useState<string>('UTC');
  const [toTimeZone, setToTimeZone] = useState<string>('Asia/Kolkata');
  const [selectedTimeZone, setSelectedTimeZone] = useState<string>('UTC');
  const [epochInput, setEpochInput] = useState<string>('');

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Update converted times when input changes
  useEffect(() => {
    if (!inputTime && !epochInput) {
      setConvertedTimes({});
      return;
    }

    let date: Date;
    if (epochInput) {
      const timestamp = parseInt(epochInput);
      if (isNaN(timestamp)) {
        setConvertedTimes({});
        return;
      }
      date = new Date(timestamp);
    } else {
      date = new Date(inputTime);
    }

    if (isNaN(date.getTime())) {
      setConvertedTimes({});
      return;
    }

    const times: { [key: string]: string } = {};
    timeZones.forEach(tz => {
      try {
        times[tz.value] = date.toLocaleString('en-US', {
          timeZone: tz.value,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false,
          timeZoneName: 'short'
        });
      } catch (error) {
        times[tz.value] = 'Invalid timezone';
      }
    });
    setConvertedTimes(times);
  }, [inputTime, epochInput]);

  const handleDateTimeInput = (value: string) => {
    setInputTime(value);
    const date = new Date(value);
    setEpochInput(date.getTime().toString());
  };

  const formatCurrentTime = (timeZone: string) => {
    try {
      const time = currentTime.toLocaleString('en-US', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
        timeZoneName: 'short'
      });
      const milliseconds = currentTime.getMilliseconds().toString().padStart(3, '0');
      return `${time}.${milliseconds}`;
    } catch (error) {
      return 'Invalid timezone';
    }
  };

  const renderCurrentTime = () => (
    <div className="space-y-6">
      <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Current Time</h3>
          <div className="text-sm font-mono bg-gray-100 dark:bg-gray-800 px-3 py-1 rounded-lg text-gray-600 dark:text-gray-300">
            Epoch: {currentTime.getTime()}
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {timeZones.map((tz) => (
            <Clock
              key={tz.value}
              time={currentTime}
              timeZone={tz.value}
              label={tz.label}
              offset={tz.offset}
            />
          ))}
        </div>
      </div>
    </div>
  );

  const renderTimezoneConverter = () => (
    <div className="space-y-6">
      <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Time Zone Converter</h3>
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Enter Date and Time (UTC)
              </label>
              <input
                type="datetime-local"
                value={inputTime}
                onChange={(e) => {
                  setInputTime(e.target.value);
                  const date = new Date(e.target.value);
                  if (!isNaN(date.getTime())) {
                    setEpochInput(date.getTime().toString());
                  }
                }}
                className="w-full p-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                step="1"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Enter Epoch Milliseconds
              </label>
              <input
                type="number"
                value={epochInput}
                onChange={(e) => {
                  setEpochInput(e.target.value);
                  const timestamp = parseInt(e.target.value);
                  if (!isNaN(timestamp)) {
                    const date = new Date(timestamp);
                    setInputTime(date.toISOString().slice(0, 19));
                  }
                }}
                placeholder="e.g., 1710864000000"
                className="w-full p-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
              {epochInput && !isNaN(parseInt(epochInput)) && (
                <div className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  {new Date(parseInt(epochInput)).toISOString()}
                </div>
              )}
            </div>
          </div>

          {Object.keys(convertedTimes).length > 0 && (
            <div className="mt-6">
              <h4 className="text-md font-medium text-gray-700 dark:text-gray-300 mb-4">
                Converted Times in All Timezones
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {timeZones.map((tz) => (
                  <div key={tz.value} className="p-3 bg-white dark:bg-gray-800 rounded-lg">
                    <div className="flex justify-between items-center">
                      <div className="text-sm font-medium text-gray-500 dark:text-gray-400">{tz.label}</div>
                      <div className="text-xs text-gray-400 dark:text-gray-500">{tz.offset}</div>
                    </div>
                    <div className="text-lg text-gray-900 dark:text-white mt-1">
                      {convertedTimes[tz.value]}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const getTimeZoneOffset = (from: string, to: string) => {
    try {
      const now = new Date();
      const fromTime = new Date(now.toLocaleString('en-US', { timeZone: from }));
      const toTime = new Date(now.toLocaleString('en-US', { timeZone: to }));
      
      const diffMs = toTime.getTime() - fromTime.getTime();
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      
      const sign = diffHours >= 0 ? '+' : '-';
      const absHours = Math.abs(diffHours);
      const absMinutes = Math.abs(diffMinutes);
      
      return `${sign}${absHours.toString().padStart(2, '0')}:${absMinutes.toString().padStart(2, '0')}`;
    } catch (error) {
      return 'Invalid timezone';
    }
  };

  const renderTimeZoneOffset = () => (
    <div className="space-y-6">
      <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Time Zone Offset Calculator</h3>
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                From Time Zone
              </label>
              <select
                value={fromTimeZone}
                onChange={(e) => setFromTimeZone(e.target.value)}
                className="w-full p-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                {timeZones.map((tz) => (
                  <option key={tz.value} value={tz.value}>
                    {tz.label} ({tz.offset})
                  </option>
                ))}
              </select>
              <div className="mt-2 p-3 bg-white dark:bg-gray-800 rounded-lg">
                <div className="text-sm text-gray-500 dark:text-gray-400">Current Time</div>
                <div className="text-lg text-gray-900 dark:text-white">
                  {currentTime.toLocaleString('en-US', {
                    timeZone: fromTimeZone,
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                    hour12: false
                  })}
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                To Time Zone
              </label>
              <select
                value={toTimeZone}
                onChange={(e) => setToTimeZone(e.target.value)}
                className="w-full p-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                {timeZones.map((tz) => (
                  <option key={tz.value} value={tz.value}>
                    {tz.label} ({tz.offset})
                  </option>
                ))}
              </select>
              <div className="mt-2 p-3 bg-white dark:bg-gray-800 rounded-lg">
                <div className="text-sm text-gray-500 dark:text-gray-400">Current Time</div>
                <div className="text-lg text-gray-900 dark:text-white">
                  {currentTime.toLocaleString('en-US', {
                    timeZone: toTimeZone,
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                    hour12: false
                  })}
                </div>
              </div>
            </div>
          </div>

          <div className="p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="text-center">
              <div className="text-sm text-gray-500 dark:text-gray-400 mb-2">Time Difference</div>
              <div className="text-2xl font-mono text-gray-900 dark:text-white">
                {getTimeZoneOffset(fromTimeZone, toTimeZone)}
              </div>
              <div className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                {timeZones.find(tz => tz.value === toTimeZone)?.label} is{' '}
                {getTimeZoneOffset(fromTimeZone, toTimeZone)} hours{' '}
                {getTimeZoneOffset(fromTimeZone, toTimeZone).startsWith('+') ? 'ahead' : 'behind'} of{' '}
                {timeZones.find(tz => tz.value === fromTimeZone)?.label}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {timeZones.map((tz) => (
              <div key={tz.value} className="p-3 bg-white dark:bg-gray-800 rounded-lg">
                <div className="flex justify-between items-center">
                  <div className="text-sm font-medium text-gray-500 dark:text-gray-400">{tz.label}</div>
                  <div className="text-xs text-gray-400 dark:text-gray-500">
                    {getTimeZoneOffset(fromTimeZone, tz.value)}
                  </div>
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                  {currentTime.toLocaleString('en-US', {
                    timeZone: tz.value,
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const handleTimeZoneSelect = (timeZone: string) => {
    setSelectedTimeZone(timeZone);
    if (activeTab === 'timezone') {
      setFromTimeZone(timeZone);
    } else if (activeTab === 'offset') {
      setFromTimeZone(timeZone);
    }
  };

  const renderTimeZoneMap = () => (
    <div className="space-y-6">
      <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Time Zone Map</h3>
        <div className="space-y-4">
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg">
            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Quick Actions</h4>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => handleTimeZoneSelect('UTC')}
                className={`px-3 py-1 rounded-md text-sm ${
                  selectedTimeZone === 'UTC'
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                }`}
              >
                UTC
              </button>
              <button
                onClick={() => handleTimeZoneSelect('Asia/Kolkata')}
                className={`px-3 py-1 rounded-md text-sm ${
                  selectedTimeZone === 'Asia/Kolkata'
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                }`}
              >
                IST
              </button>
              <button
                onClick={() => handleTimeZoneSelect('America/New_York')}
                className={`px-3 py-1 rounded-md text-sm ${
                  selectedTimeZone === 'America/New_York'
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                }`}
              >
                EST
              </button>
              <button
                onClick={() => handleTimeZoneSelect('Europe/London')}
                className={`px-3 py-1 rounded-md text-sm ${
                  selectedTimeZone === 'Europe/London'
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                }`}
              >
                GMT
              </button>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg">
            <div className="flex justify-between items-center mb-4">
              <div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Selected Time Zone</div>
                <div className="text-lg font-medium text-gray-900 dark:text-white">
                  {timeZones.find(tz => tz.value === selectedTimeZone)?.label || selectedTimeZone}
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Current Time</div>
                <div className="text-lg font-medium text-gray-900 dark:text-white">
                  {currentTime.toLocaleString('en-US', {
                    timeZone: selectedTimeZone,
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                    hour12: false
                  })}
                </div>
              </div>
            </div>
          </div>
          
          <TimeZoneMap
            currentTime={currentTime}
            selectedTimeZone={selectedTimeZone}
            onTimeZoneSelect={handleTimeZoneSelect}
          />
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Helmet>
        <title>Time Utility | Time Zone Converter & World Clock - Davinqi</title>
        <meta name="description" content="Convert time zones, calculate time differences, and view world time map with Davinqi Time Utility. Accurate and easy to use." />
        <meta property="og:title" content="Time Utility | Time Zone Converter & World Clock - Davinqi" />
        <meta property="og:description" content="Convert time zones, calculate time differences, and view world time map with Davinqi Time Utility. Accurate and easy to use." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://davinqi.com/time" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Time Utility | Time Zone Converter & World Clock - Davinqi" />
        <meta name="twitter:description" content="Convert time zones, calculate time differences, and view world time map with Davinqi Time Utility. Accurate and easy to use." />
      </Helmet>
      <div className="container mx-auto px-4 pb-4 -mt-5">
        <div className="max-w-6xl mx-auto">
          <div className="flex justify-end mb-2">
            <div className="flex space-x-2">
              <button
                onClick={() => setActiveTab('current')}
                className={`px-4 py-2 rounded-lg flex items-center space-x-2 ${
                  activeTab === 'current'
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                }`}
              >
                {/*@ts-ignore*/}
                <FaClock />
                <span>Current Time</span>
              </button>
              <button
                onClick={() => setActiveTab('timezone')}
                className={`px-4 py-2 rounded-lg flex items-center space-x-2 ${
                  activeTab === 'timezone'
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                }`}
              >
                {/*@ts-ignore*/}
                <FaExchangeAlt />
                <span>Timezone Converter</span>
              </button>
              <button
                onClick={() => setActiveTab('offset')}
                className={`px-4 py-2 rounded-lg flex items-center space-x-2 ${
                  activeTab === 'offset'
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                }`}
              >
                {/*@ts-ignore*/}
                <FaHourglassHalf />
                <span>Time Difference</span>
              </button>
              <button
                onClick={() => setActiveTab('map')}
                className={`px-4 py-2 rounded-lg flex items-center space-x-2 ${
                  activeTab === 'map'
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                }`}
              >
                {/*@ts-ignore*/}
                <FaMapMarkedAlt />
                <span>Time Zone Map</span>
              </button>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
            {activeTab === 'current' && renderCurrentTime()}
            {activeTab === 'timezone' && renderTimezoneConverter()}
            {activeTab === 'offset' && renderTimeZoneOffset()}
            {activeTab === 'map' && renderTimeZoneMap()}
          </div>
        </div>
      </div>
    </>
  );
};

export default TimeUtility; 